<script setup>
  import { ref, computed, watch } from "vue";
  import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
  import { Button } from "@/components/ui/button";
  import { Input } from "@/components/ui/input";
  import { Label } from "@/components/ui/label";
  import { Switch } from "@/components/ui/switch";
  import { Select } from "@/components/episto-ui/select";
  import { Loader2, Upload, Download, FileSpreadsheet, X } from "lucide-vue-next";

  const props = defineProps({
    mode: {
      type: String,
      default: "create",
      validator: (v) => ["create", "edit"].includes(v),
    },
    initialName: {
      type: String,
      default: "",
    },
    initialActive: {
      type: Boolean,
      default: true,
    },
    initialAccountIds: {
      type: Array,
      default: () => [],
    },
    initialIsSystem: {
      type: Boolean,
      default: false,
    },
    accountOptions: {
      type: Array,
      default: () => [],
    },
    initialQualitative: {
      type: Boolean,
      default: false,
    },
    pptxSource: {
      type: Object,
      default: null,
    },
    submitting: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: null,
    },
  });

  const emit = defineEmits([
    "submit",
    "download",
    "update:name",
    "update:active",
    "update:accountIds",
    "update:isSystem",
    "update:qualitative",
  ]);

  const themeName = ref(props.initialName);
  const isActive = ref(props.initialActive);
  const selectedAccountIds = ref(props.initialAccountIds.map(String));
  const isSystem = ref(props.initialIsSystem);
  const isQualitative = ref(props.initialQualitative);
  const selectedFile = ref(null);
  const fileInput = ref(null);

  const isCreateMode = computed(() => props.mode === "create");

  const canSubmit = computed(() => {
    if (isCreateMode.value) return themeName.value.trim() && selectedFile.value;
    return selectedFile.value;
  });

  const showSubmitButton = computed(() => {
    if (isCreateMode.value) return true;
    return selectedFile.value;
  });

  watch(themeName, (value) => emit("update:name", value));

  watch(
    () => props.initialName,
    (value) => {
      themeName.value = value;
    },
  );

  watch(
    () => props.initialActive,
    (value) => {
      isActive.value = value;
    },
  );

  watch(
    () => props.initialAccountIds,
    (value) => {
      selectedAccountIds.value = value.map(String);
    },
  );

  watch(
    () => props.initialIsSystem,
    (value) => {
      isSystem.value = value;
    },
  );

  watch(
    () => props.initialQualitative,
    (value) => {
      isQualitative.value = value;
    },
  );

  function handleActiveChange(value) {
    isActive.value = value;
    emit("update:active", value);
  }

  function handleAccountsChange(value) {
    selectedAccountIds.value = value;
    emit("update:accountIds", value.map(Number));
  }

  function handleIsSystemChange(value) {
    isSystem.value = value;
    emit("update:isSystem", value);
  }

  function handleQualitativeChange(value) {
    isQualitative.value = value;
    emit("update:qualitative", value);
  }

  function triggerFileInput() {
    fileInput.value?.click();
  }

  function handleFileSelect(event) {
    const file = event.target.files[0];
    if (file) selectedFile.value = file;
    event.target.value = "";
  }

  function clearSelectedFile() {
    selectedFile.value = null;
  }

  function handleSubmit() {
    emit("submit", {
      name: themeName.value.trim(),
      file: selectedFile.value,
      accountIds: selectedAccountIds.value.map(Number),
      isSystem: isSystem.value,
    });
  }

  function reset() {
    selectedFile.value = null;
  }

  defineExpose({ reset });
</script>

<template>
  <Card class="w-full max-w-2xl p-6">
    <CardHeader class="p-0 pb-6">
      <CardTitle class="heading-page">{{ isCreateMode ? "New Master Report" : "Setup" }}</CardTitle>
      <CardDescription>
        {{ isCreateMode ? "Create a new master report from a PowerPoint file." : "" }}
      </CardDescription>
    </CardHeader>
    <CardContent class="p-0 space-y-6">
      <!-- Theme Name -->
      <div>
        <Label for="theme-name">Name</Label>
        <Input
          id="theme-name"
          v-model="themeName"
          class="mt-2 w-1/2" />
      </div>

      <!-- Accounts -->
      <div>
        <Label>Accounts</Label>
        <Select
          :model-value="selectedAccountIds"
          :options="accountOptions"
          multiple
          filterable
          placeholder="Select accounts"
          class="mt-2 w-1/2"
          @update:model-value="handleAccountsChange" />
      </div>

      <!-- System toggle -->
      <div class="flex items-center gap-3">
        <Switch
          :model-value="isSystem"
          @update:model-value="handleIsSystemChange" />
        <Label class="cursor-pointer">System (shared with all accounts)</Label>
      </div>

      <!-- Active toggle (edit mode only) -->
      <div
        v-if="!isCreateMode"
        class="flex items-center gap-3">
        <Switch
          :model-value="isActive"
          @update:model-value="handleActiveChange" />
        <Label class="cursor-pointer">Active</Label>
      </div>

      <!-- Qualitative toggle (edit mode only) -->
      <div
        v-if="!isCreateMode"
        class="flex items-center gap-3">
        <Switch
          :model-value="isQualitative"
          @update:model-value="handleQualitativeChange" />
        <Label class="cursor-pointer">Qualitative</Label>
      </div>

      <!-- PowerPoint File Section -->
      <div>
        <Label>PowerPoint File</Label>

        <!-- Edit mode: show current file -->
        <div
          v-if="!isCreateMode && pptxSource"
          class="mt-2">
          <p class="text-sm">{{ pptxSource.filename }}</p>
          <div class="mt-4 flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              @click="$emit('download')">
              <Download class="w-4 h-4 mr-2" />
              Download PPTX
            </Button>
            <Button
              variant="outline"
              size="sm"
              @click="triggerFileInput">
              <Upload class="w-4 h-4 mr-2" />
              Replace PPTX
            </Button>
          </div>
        </div>

        <!-- Create mode or no pptx source: file upload -->
        <div
          v-else
          class="mt-2 flex items-center gap-3">
          <Button
            variant="outline"
            @click="triggerFileInput">
            <Upload class="w-4 h-4 mr-2" />
            {{ selectedFile ? "Change file" : "Upload PPTX" }}
          </Button>
          <span
            v-if="selectedFile"
            class="text-sm text-muted-foreground">
            {{ selectedFile.name }}
          </span>
        </div>

        <!-- Hidden file input -->
        <input
          ref="fileInput"
          type="file"
          accept=".pptx"
          class="hidden"
          @change="handleFileSelect" />

        <!-- Show selected file for replace -->
        <div
          v-if="!isCreateMode && selectedFile"
          class="mt-4 flex items-center gap-2 p-3 rounded-lg bg-muted/50">
          <FileSpreadsheet class="w-5 h-5 text-muted-foreground" />
          <span class="text-sm">{{ selectedFile.name }}</span>
          <Button
            variant="ghost"
            size="sm"
            class="ml-auto h-6 w-6 p-0"
            @click="clearSelectedFile">
            <X class="w-4 h-4" />
          </Button>
        </div>
      </div>

      <!-- Error message -->
      <p
        v-if="error"
        class="text-sm text-destructive">
        {{ error }}
      </p>

      <!-- Actions -->
      <div
        v-if="showSubmitButton"
        class="pt-2">
        <Button
          :disabled="!canSubmit || submitting"
          @click="handleSubmit">
          <Loader2
            v-if="submitting"
            class="w-4 h-4 mr-2 animate-spin" />
          {{ submitting ? "Importing..." : "Import" }}
        </Button>
      </div>
    </CardContent>
  </Card>
</template>
